import { ReactElement, useContext, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Dropdown } from "@screencloud/screencloud-ui-components";
import { EditorStore } from "../../types";
import { RootState } from "../../reducers";
import { AuthMode } from "../../types";
import { addPublicPrivateAuth } from "../../actions";
import "./Share.scss";
import { showLiveStreamingPageUrl } from "../../utils";
import ThirdPartyAuth from "../../components/ThirdPartyAuth/ThirdPartyAuth";
import PasswordAuth from "../../components/PasswordAuth/PasswordAuth";
import { ScreenCloudContext } from "../../ScreenCloudReactApp";

const authenticationModes = [
  {
    key: AuthMode.Private,
    value: AuthMode.Private,
    text: "Private Stream",
    helptext:
      "This stream type allows you to stream only to ScreenCloud screens, disabling the public URL to others. This is the ScreenCloud default.",
  },
  {
    key: AuthMode.Public,
    value: AuthMode.Public,
    text: "Public Stream",
    helptext:
      "Use Public Stream setting to create a completely public URL. Your broadcast will be streamed on ScreenCloud screens as well to those who have the URL.",
  },
  {
    key: AuthMode.Slack,
    value: AuthMode.Slack,
    text: "Slack Authenticated Stream",
    helptext:
      "Protect your URL with Slack. Users with this URL will only be able to access your broadcast if they are part of the same workspace. You can still broadcast on ScreenCloud screens.",
  },
  {
    key: AuthMode.Teams,
    value: AuthMode.Teams,
    text: "Teams Authenticated Stream",
    helptext:
      "Protect your URL with Microsoft Teams. Users with this URL will only be able to access your broadcast if they are part of the same workspace. You can still broadcast on ScreenCloud screens.",
  },
  {
    key: AuthMode.Password,
    value: AuthMode.Password,
    text: "Password Protected Stream",
    helptext:
      "Set a unique password to protect your stream URL. Anyone with this password will be able to view your broadcasts. Broadcasts can still be viewed on ScreenCloud screens",
  },
];

const Share = (): ReactElement<{}> => {
  const dispatch = useDispatch();
  const editor: EditorStore = useSelector((state: RootState) => state.editor);
  const { logger } = useContext(ScreenCloudContext);

  const [authMode, setAuthMode] = useState(
    editor.channel ? editor.channel.authMode : AuthMode.Private
  );

  const [selectedIndex, setSelectedIndex] = useState(
    authenticationModes.findIndex((a) =>
      editor.channel ? a.key === editor.channel.authMode : false
    ) || 0
  );

  const { channel } = editor;

  const authModeChanged = (mode: AuthMode): void => {
    setAuthMode(mode);
    setSelectedIndex(authenticationModes.findIndex((a) => a.key === mode));

    if (mode === AuthMode.Public || mode === AuthMode.Private) {
      const { channel } = editor;

      if (channel) {
        const { screenCloudOrgId, channelId, spaceId } = channel;
        dispatch(
          addPublicPrivateAuth(
            {
              screenCloudOrgId,
              channelId,
              spaceId,
              authMode: mode,
              orgId: "",
              orgName: "",
              password: "",
            },
            logger
          )
        );
      }
    }
  };

  return channel ? (
    <div className="privacy-auth">
      <Dropdown
        selection
        fluid
        placeholder="Select authentication"
        options={authenticationModes}
        value={authMode}
        onChange={(_, data) => {
          authModeChanged(data.value as AuthMode);
        }}
      />
      <div className="auth-help-text">
        <div className="header">{authenticationModes[selectedIndex].text}</div>
        <span className="description">
          {authenticationModes[selectedIndex].helptext}
        </span>
      </div>
      {authMode !== AuthMode.Private && (
        <div className="grid yellow">
          {(authMode === AuthMode.Slack || authMode === AuthMode.Teams) && (
            <ThirdPartyAuth authMode={authMode} />
          )}
          {authMode === AuthMode.Password && <PasswordAuth />}
          {showLiveStreamingPageUrl(authMode) && (
            <div className="row">
              <div className="label flex">Live streaming page URL</div>
              <Button
                className="wide-button"
                primary
                onClick={() => {
                  if (editor.channel) {
                    window.open(
                      `${process.env.REACT_APP_BASE_URL}/view/${editor.channel.screenCloudOrgId}/${editor.channel.spaceId}/${editor.channel.channelId}`
                    );
                  }
                }}
              >
                Go to live streaming page
              </Button>
            </div>
          )}
          {editor.channel &&
            (editor.channel.authMode === AuthMode.Slack ||
              editor.channel.authMode === AuthMode.Teams) &&
            editor.channel.orgName && (
              <div className="row">
                <div className="label flex">Authenticated workspace</div>
                <span className="workspace-name">{editor.channel.orgName}</span>
              </div>
            )}
        </div>
      )}
    </div>
  ) : (
    <div />
  );
};

export default Share;
